// TerminNotizenPage.tsx — Termin-Notizen als eigene Seite im OneNote-Stil
// (Termine-SP7/A12, Route /termine/notizen/:terminId — terminId = Sidecar-Id).
//
// Aufbau (Plan 3.8): Kopf (Termin-Betreff, Zeit, Zurück, „Termin öffnen") +
// freie Seite mit drei Ebenen:
//   1. Freitext: contentEditable-Fläche mit minimaler eigener Toolbar, OHNE
//      Rich-Text-Editor-Dependency (react-quill-CVE-Entscheid bleibt). Fett/
//      Kursiv laufen über die Range-API; die AUFZÄHLUNG seit PO 14.09.2026
//      über `execCmd` aus `mail/richtext/editorCommands.ts` — derselbe Weg,
//      den die V4-Notizblöcke (`BlockKontextmenue`) und der Mail-Editor gehen.
//      Der frühere Vorsatz „bewusst ohne execCommand" ist damit überholt: das
//      Repo hat die Deprecation an EINER Stelle gekapselt, und nur der Browser
//      beherrscht Toggle, Enter-Weiterführung und Verschachtelung einer Liste
//      verlässlich. Die Tastenlogik (`- ` + Leertaste, Tab/Shift+Tab, Enter
//      auf leerem Punkt) kommt aus `listenTasten.ts`.
//      Gespeichert wird sanitisiertes HTML; autoritativ sanitisiert der Server.
//   2. Zeichnen (Zeichnen-MVP, Entscheidung 66): Pointer-Events-Canvas als
//      Layer ÜBER dem Text (Apple-Pencil-tauglich, touch-action:none),
//      Freihand-Stift in 3 Farben + Radierer; Strokes als Vektor-JSON →
//      nachträglich einzeln löschbar/weiterzeichenbar.
//   3. Dokumente: bestehender documentService-Upload mit calendarTerminId
//      (identisch zum Termin-Fenster-Sidecar — Uploads erscheinen dort mit).
//
// Rechte: GET liefert `canWrite` (Kalender-Schreibrecht des Sidecar-Postfachs);
// read-only sieht alle Inhalte, sämtliche Schreib-Controls sind aus — final
// sichert das Backend ab (assertCalendarWrite beim PUT/Upload).

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import DOMPurify from "dompurify";
import {
    ArrowLeft20Regular,
    CalendarLtr20Regular,
    DocumentAdd20Regular,
    Eraser20Regular,
    TextBold20Regular,
    TextBulletListLtr20Regular,
    TextItalic20Regular,
} from "@fluentui/react-icons";
import { useSmartBack } from "../../hooks/smartBack";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openTerminFormWindow } from "../windowManager/openTerminFormWindow";
import {
    calendarTerminService,
    type NotizDrawingDto,
    type TerminMetaDto,
    type TerminNotizSeiteDto,
} from "../../services/calendarTerminService";
import { calendarService, type CalendarEventDetail } from "../../services/calendarService";
import { gridEventAusTermin } from "./gridEvent";
import { TerminPeekPopover, type TerminPeek } from "./TerminPeekPopover";
import {
    NeuerVorgangAusTerminDialog,
    VorgangZuweisenDialog,
    type TerminVorgangKontext,
} from "./TerminVorgangDialog";
import { documentService, type DocumentDto } from "../../services/documentService";
import { vorgangService } from "../../services/vorgangService";
import { execCmd } from "../mail/richtext/editorCommands";
import { behandleListenTaste } from "../mail/richtext/listenTasten";
import { toGraphDate } from "../dashboard/DashboardShared";
import { graphAllDayDate } from "./allDay";
import { zeitraumLabel } from "./peek";
import {
    NOTIZ_STIFT_BREITE,
    NOTIZ_STIFT_FARBEN,
    neueStrokeId,
    normalizeDrawing,
    radiereAnPunkt,
    zeichnungsHoehe,
    type NotizPunkt,
} from "./notizSeite";
import "./calendar.css";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

/** Werkzeug der Seite: Text tippen, Freihand-Stift oder Radierer. */
type NotizWerkzeug = "text" | "stift" | "radierer";

type SaveStatus = "leer" | "dirty" | "speichert" | "gespeichert" | "fehler";

/**
 * FE-Sanitizing des geladenen HTML vor dem Einsetzen in die contentEditable-
 * Fläche (Belt-and-suspenders: der Server sanitisiert beim Speichern bereits
 * autoritativ via sanitize-html — XSS-Leitplanke wie beim Mail-Rework).
 */
function sanitizeNotizHtml(html: string): string {
    return DOMPurify.sanitize(html ?? "", {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "style"],
        FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction"],
    });
}

/* ── Inline-Formatierung über die Range-API (Fett/Kursiv) ─────────────────── */

/** Nächstes Ancestor-Element mit Tag `tagName` INNERHALB des Editors, sonst null. */
function findeFormatAncestor(
    node: Node | null,
    tagName: string,
    editor: HTMLElement,
): HTMLElement | null {
    let cur: Node | null = node;
    while (cur && cur !== editor) {
        if (istDom(cur, "HTMLElement") && cur.tagName.toLowerCase() === tagName) return cur;
        cur = cur.parentNode;
    }
    return null;
}

/** Element durch seine Kinder ersetzen (Format-Toggle „aus"). */
function unwrapElement(el: HTMLElement): void {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
}

/**
 * Inline-Format (strong/em) auf die aktuelle Selektion anwenden bzw. wieder
 * entfernen (Toggle). Bewusst minimal (SP7-Toolbar-MVP): kollabierte Selektion
 * = No-Op; Selektion über Blockgrenzen wird per extractContents gewrappt.
 */
function toggleInlineFormat(editor: HTMLElement, tagName: "strong" | "em"): void {
    const sel = aktiveAuswahl();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;

    // Toggle „aus": Selektion liegt komplett in einem bestehenden Format-Tag.
    const vorhanden = findeFormatAncestor(range.commonAncestorContainer, tagName, editor);
    if (vorhanden) {
        unwrapElement(vorhanden);
        return;
    }
    if (range.collapsed) return;

    const el = document.createElement(tagName);
    try {
        range.surroundContents(el);
    } catch {
        // Selektion über Element-Grenzen: Inhalt herausziehen und gewrappt einsetzen.
        el.appendChild(range.extractContents());
        range.insertNode(el);
    }
    // Selektion auf den formatierten Inhalt setzen (weitertippen bleibt im Tag).
    sel.removeAllRanges();
    const neu = aktivesDokument().createRange();
    neu.selectNodeContents(el);
    sel.addRange(neu);
}

/**
 * Aufzählung ein- bzw. ausschalten (PO 14.09.2026: „Aufzählungen mit
 * automatischer Einrückung"). Bis dahin baute die Seite `<ul><li>` von Hand
 * per `range.insertNode` — ohne Toggle, ohne Einrückung, und Enter lief nur
 * über das native Verhalten. `insertUnorderedList` ist das, was
 * `BlockKontextmenue` in V4 nutzt: der Browser umschließt ganze Absätze,
 * führt die Liste bei Enter weiter und schaltet sie beim zweiten Klick ab.
 */
function fuegeListeEin(editor: HTMLElement): void {
    execCmd(editor, "insertUnorderedList");
}

/* ── Seite ────────────────────────────────────────────────────────────────── */

const AUTOSAVE_MS = 1200;
const MIN_FLAECHE_PX = 480;

export const TerminNotizenPage: React.FC = () => {
    const { terminId: terminIdParam } = useParams<{ terminId: string }>();
    const terminId = Number(terminIdParam);
    const goBack = useSmartBack("/termine");
    const navigate = useNavigate();
    const { openWindow } = useWindowManager();

    const [seite, setSeite] = useState<TerminNotizSeiteDto | null>(null);
    const [ladeFehler, setLadeFehler] = useState<string | null>(null);
    const [terminKopf, setTerminKopf] = useState<{ betreff: string; zeitraum: string } | null>(
        null,
    );
    // Der volle Termin und seine Sidecar-Meta: Grundlage fuer den Peek
    // („Termin anzeigen") und fuer die Vorgangs-Zeile unter der Notiz.
    const [termin, setTermin] = useState<CalendarEventDetail | null>(null);
    const [meta, setMeta] = useState<TerminMetaDto | null>(null);
    /** Betreff des verknüpften To-dos (PO 14.09.2026) — best effort, sonst nur die Id. */
    const [todoTitel, setTodoTitel] = useState<string | null>(null);
    const [peek, setPeek] = useState<TerminPeek | null>(null);
    const [zuweisenOffen, setZuweisenOffen] = useState(false);
    const [neuerVorgangOffen, setNeuerVorgangOffen] = useState(false);
    const [drawing, setDrawing] = useState<NotizDrawingDto>({ strokes: [] });
    const [werkzeug, setWerkzeug] = useState<NotizWerkzeug>("text");
    const [stiftFarbe, setStiftFarbe] = useState<string>(NOTIZ_STIFT_FARBEN[0].hex);
    const [status, setStatus] = useState<SaveStatus>("leer");
    const [docs, setDocs] = useState<DocumentDto[]>([]);
    const [uploading, setUploading] = useState(false);

    const editorRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const flaecheRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Spiegel des Drawing-States für Pointer-Handler (kein stale Closure) und
    // laufender (noch nicht committeter) Stroke.
    const drawingRef = useRef<NotizDrawingDto>({ strokes: [] });
    const aktiverStrokeRef = useRef<{ id: string; color: string; width: number; points: NotizPunkt[] } | null>(null);
    const saveTimerRef = useRef<number | null>(null);
    const [canvasGroesse, setCanvasGroesse] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

    const canWrite = seite?.canWrite === true;
    const canLink = seite?.canLink === true;
    /**
     * PO 28.09.2026 — nur über den FO/BO-Kontakt-Zugang sichtbar: `canWrite`
     * gilt dann AUSSCHLIESSLICH für den Seiteninhalt (Text/Zeichnung).
     * Dokumente, Löschen und Bearbeiten des Termins hängen am Kalenderrecht.
     */
    const nurKontaktZugang = seite?.zugang === "kontakt";
    const canWriteKalender = canWrite && !nurKontaktZugang;

    /* ── Laden ── */
    useEffect(() => {
        if (!Number.isFinite(terminId) || terminId <= 0) {
            setLadeFehler("Ungültige Termin-Id.");
            return;
        }
        let aktiv = true;
        calendarTerminService
            .getNotizSeite(terminId)
            .then((dto) => {
                if (!aktiv) return;
                setSeite(dto);
                const d = normalizeDrawing(dto.drawing);
                setDrawing(d);
                drawingRef.current = d;
                // Freitext einmalig in die contentEditable-Fläche setzen (danach
                // ist das DOM die Quelle — React fasst den Inhalt nicht mehr an).
                if (editorRef.current) {
                    editorRef.current.innerHTML = sanitizeNotizHtml(dto.contentHtml ?? "");
                }
                // Betreff/Zeit best-effort über die bestehende Event-Route.
                // Die Sidecar-Meta traegt den verknuepften Vorgang (K9) und die
                // Art-Kennzeichnung — best effort, ohne sie fehlt nur Beiwerk.
                calendarTerminService
                    .getMeta(dto.mailbox, dto.graphEventId)
                    .then((m) => aktiv && setMeta(m))
                    .catch(() => undefined);
                calendarService
                    .getEvent(dto.mailbox, dto.graphEventId)
                    .then((ev) => {
                        if (!aktiv) return;
                        setTermin(ev);
                        const start = ev.isAllDay
                            ? graphAllDayDate(ev.start.dateTime)
                            : toGraphDate(ev.start.dateTime);
                        const end = ev.isAllDay
                            ? graphAllDayDate(ev.end.dateTime)
                            : toGraphDate(ev.end.dateTime);
                        setTerminKopf({
                            betreff: ev.subject || "(ohne Titel)",
                            zeitraum: zeitraumLabel(start, end, ev.isAllDay),
                        });
                    })
                    .catch(() => undefined);
            })
            .catch((err) => {
                console.error("[TerminNotizenPage] Laden fehlgeschlagen", err);
                if (aktiv) {
                    setLadeFehler(
                        "Notiz-Seite konnte nicht geladen werden (kein Zugriff oder Termin unbekannt).",
                    );
                }
            });
        return () => {
            aktiv = false;
        };
    }, [terminId]);

    /* ── Dokumente (Reuse documentService, wie TerminSidecar) ── */
    const ladeDocs = useCallback(() => {
        if (!Number.isFinite(terminId) || terminId <= 0) return;
        documentService
            .list({ calendarTerminId: terminId })
            .then(setDocs)
            .catch((e) => console.error(e));
    }, [terminId]);

    useEffect(() => {
        ladeDocs();
    }, [ladeDocs]);

    /* ── Speichern (debounced Autosave + expliziter Button) ── */
    const speichern = useCallback(async () => {
        if (!seite || !seite.canWrite) return;
        if (saveTimerRef.current != null) {
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        const html = editorRef.current?.innerHTML ?? "";
        const d = drawingRef.current;
        setStatus("speichert");
        try {
            await calendarTerminService.saveNotizSeite(seite.terminId, {
                contentHtml: html.trim().length > 0 ? html : null,
                drawing: d.strokes.length > 0 ? d : null,
            });
            setStatus("gespeichert");
        } catch (err) {
            console.error("[TerminNotizenPage] Speichern fehlgeschlagen", err);
            setStatus("fehler");
        }
    }, [seite]);

    const markDirty = useCallback(() => {
        if (!seite?.canWrite) return;
        setStatus("dirty");
        if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => {
            void speichern();
        }, AUTOSAVE_MS);
    }, [seite, speichern]);

    // Ausstehenden Autosave-Timer beim Unmount lösen (kein setState-Leak).
    useEffect(
        () => () => {
            if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
        },
        [],
    );

    /* ── Canvas: Größe an die Fläche koppeln + Strokes zeichnen ── */
    useEffect(() => {
        const el = flaecheRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => {
            setCanvasGroesse({ w: el.clientWidth, h: el.clientHeight });
        });
        ro.observe(el);
        setCanvasGroesse({ w: el.clientWidth, h: el.clientHeight });
        return () => ro.disconnect();
    }, [seite]);

    const zeichneAlles = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const { w, h } = canvasGroesse;
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const alle = aktiverStrokeRef.current
            ? [...drawing.strokes, aktiverStrokeRef.current]
            : drawing.strokes;
        for (const s of alle) {
            if (s.points.length === 0) continue;
            ctx.strokeStyle = s.color;
            ctx.lineWidth = s.width;
            ctx.beginPath();
            ctx.moveTo(s.points[0][0], s.points[0][1]);
            if (s.points.length === 1) {
                // Einzelpunkt (Tap): als Punkt sichtbar machen.
                ctx.lineTo(s.points[0][0] + 0.1, s.points[0][1] + 0.1);
            } else {
                for (let i = 1; i < s.points.length; i++) {
                    ctx.lineTo(s.points[i][0], s.points[i][1]);
                }
            }
            ctx.stroke();
        }
    }, [drawing, canvasGroesse]);

    useEffect(() => {
        zeichneAlles();
    }, [zeichneAlles]);

    /* ── Pointer-Zeichnen (Apple Pencil/Touch/Maus; touch-action:none) ── */
    const pointerPos = (e: React.PointerEvent): NotizPunkt => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return [0, 0];
        return [e.clientX - rect.left, e.clientY - rect.top];
    };

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!canWrite || werkzeug === "text") return;
        e.preventDefault();
        (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        const p = pointerPos(e);
        if (werkzeug === "radierer") {
            const res = radiereAnPunkt(drawingRef.current, p);
            if (res.entfernt) {
                drawingRef.current = res.drawing;
                setDrawing(res.drawing);
                markDirty();
            }
            return;
        }
        aktiverStrokeRef.current = {
            id: neueStrokeId(),
            color: stiftFarbe,
            width: NOTIZ_STIFT_BREITE,
            points: [p],
        };
        zeichneAlles();
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!canWrite || werkzeug === "text") return;
        if (e.buttons === 0) return;
        const p = pointerPos(e);
        if (werkzeug === "radierer") {
            const res = radiereAnPunkt(drawingRef.current, p);
            if (res.entfernt) {
                drawingRef.current = res.drawing;
                setDrawing(res.drawing);
                markDirty();
            }
            return;
        }
        const aktiv = aktiverStrokeRef.current;
        if (!aktiv) return;
        aktiv.points.push(p);
        // Nur das letzte Segment nachziehen (flüssig, ohne React-Re-Render).
        const ctx = canvasRef.current?.getContext("2d");
        const n = aktiv.points.length;
        if (ctx && n >= 2) {
            ctx.strokeStyle = aktiv.color;
            ctx.lineWidth = aktiv.width;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(aktiv.points[n - 2][0], aktiv.points[n - 2][1]);
            ctx.lineTo(aktiv.points[n - 1][0], aktiv.points[n - 1][1]);
            ctx.stroke();
        }
    };

    const onPointerUp = () => {
        const aktiv = aktiverStrokeRef.current;
        if (!aktiv) return;
        aktiverStrokeRef.current = null;
        const neu: NotizDrawingDto = { strokes: [...drawingRef.current.strokes, aktiv] };
        drawingRef.current = neu;
        setDrawing(neu);
        markDirty();
    };

    /* ── Dokumente ── */
    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !seite) return;
        setUploading(true);
        try {
            await documentService.upload({ file, calendarTerminId: seite.terminId });
            ladeDocs();
        } catch (err) {
            console.error("[TerminNotizenPage] Upload fehlgeschlagen", err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeDoc = async (id: number) => {
        try {
            await documentService.remove(id);
            ladeDocs();
        } catch (err) {
            console.error("[TerminNotizenPage] Löschen fehlgeschlagen", err);
        }
    };

    const downloadDoc = async (id: number) => {
        try {
            await documentService.openSignedDownload(id);
        } catch (err) {
            console.error("[TerminNotizenPage] Download fehlgeschlagen", err);
        }
    };

    /* ── Kopf-Aktionen ── */
    /**
     * „Termin anzeigen" zeigt den Peek — dieselbe schlanke Ansicht wie im
     * Kalender. Bis 14.09.2026 sprang der Knopf direkt in das grosse
     * Bearbeiten-Fenster; wer nur nachsehen wollte, wann der Termin ist,
     * landete im Formular. Bearbeiten fuehrt weiterhin dorthin — jetzt als
     * bewusster zweiter Schritt aus dem Peek heraus.
     */
    const zeigeTermin = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (!seite || !termin) return;
            const r = e.currentTarget.getBoundingClientRect();
            setPeek({
                event: {
                    ...gridEventAusTermin(termin, seite.mailbox, meta ?? undefined),
                    // Kontakt- oder Vorgangsweg: der Peek blendet dann das
                    // Verknüpfen aus (beides ohne Kalenderrecht).
                    ...(seite.zugang === "kontakt"
                        ? { ueberKontakt: { contactId: 0, name: "Ihr Kontakt", weg: "teilnehmer" as const } }
                        : seite.zugang === "vorgang"
                          ? { ueberKontakt: { contactId: 0, name: "den Vorgang", weg: "zuweisung" as const } }
                          : {}),
                },
                anchor: { x: Math.round(r.right), y: Math.round(r.bottom + 6) },
            });
        },
        [seite, termin, meta],
    );

    /** Aus dem Peek heraus ins volle Termin-Fenster (read-only ohne Schreibrecht). */
    const oeffneTerminFenster = useCallback(() => {
        if (!seite) return;
        setPeek(null);
        openTerminFormWindow(openWindow, {
            editEventId: seite.graphEventId,
            mailbox: seite.mailbox,
            canEdit: seite.canWrite && seite.zugang !== "kontakt",
        });
    }, [seite, openWindow]);

    /** Kontext der beiden Vorgangs-Dialoge (Auto-Bind haengt an mailbox+eventId). */
    const vorgangKontext: TerminVorgangKontext | null = seite
        ? {
              mailbox: seite.mailbox,
              eventId: seite.graphEventId,
              attendees: termin?.attendees ?? null,
              organizer: termin?.organizer ?? null,
          }
        : null;

    /** Nach dem Binden die Meta neu lesen — die Zeile zeigt dann Vorgang und To-do. */
    const metaNeuLesen = useCallback(() => {
        if (!seite) return;
        calendarTerminService
            .getMeta(seite.mailbox, seite.graphEventId)
            .then(setMeta)
            .catch(() => undefined);
    }, [seite]);

    // Der Betreff des verknüpften To-dos: die Meta trägt nur die Id. Best
    // effort — ohne ihn steht „To-do: #12", und die Zeile bleibt lesbar.
    useEffect(() => {
        const todoId = meta?.todoId ?? null;
        if (todoId == null) {
            setTodoTitel(null);
            return;
        }
        let aktiv = true;
        vorgangService
            .getTodo(todoId)
            .then((t) => aktiv && setTodoTitel(t.titel?.trim() || null))
            .catch(() => aktiv && setTodoTitel(null));
        return () => {
            aktiv = false;
        };
    }, [meta?.todoId]);

    // Zeichnung darf nie unter dem Seitenende abgeschnitten werden.
    const flaecheMinHoehe = useMemo(
        () => zeichnungsHoehe(drawing, MIN_FLAECHE_PX),
        [drawing],
    );

    // Kurz halten: der Status steht im Kopf neben den Knöpfen und wechselt beim
    // Tippen. Ein langer Text schiebt die Kopfzeile auseinander, sie bricht auf
    // dem iPad um — und die Schreibfläche rutscht unter dem Finger nach unten.
    // Der volle Wortlaut steht im `title` (Breite reserviert das CSS).
    const statusLabel: Record<SaveStatus, string> = {
        leer: "",
        dirty: "Nicht gespeichert",
        speichert: "Speichert…",
        gespeichert: "Gespeichert",
        fehler: "Fehler beim Speichern",
    };

    const statusTitel: Record<SaveStatus, string> = {
        leer: "",
        dirty: "Ungespeicherte Änderungen — wird automatisch gespeichert.",
        speichert: "Wird gespeichert…",
        gespeichert: "Alle Änderungen gespeichert.",
        fehler: "Speichern fehlgeschlagen — bitte „Speichern“ erneut drücken.",
    };

    if (ladeFehler) {
        return (
            <div className="cal-rd cal-notiz-page">
                <div className="cal-notiz-kopf">
                    <button type="button" className="cal-notiz-zurueck" onClick={goBack}>
                        <ArrowLeft20Regular aria-hidden /> Zurück
                    </button>
                </div>
                <div className="cal-notiz-fehler">{ladeFehler}</div>
            </div>
        );
    }

    return (
        <div className="cal-rd cal-notiz-page">
            {/* Kopf: Termin-Betreff + Zeit, Termin anzeigen, Save-Status. Kein
                „Zurück" mehr — der Knopf kostete im Kopf genau die Breite, die
                auf dem iPad zum Umbruch führte (zurück geht über die normale
                Navigation); im Lade-Fehler oben bleibt er als einziger Ausweg. */}
            <div className="cal-notiz-kopf">
                <div className="cal-notiz-titelblock">
                    <h1 className="cal-notiz-titel">
                        Notizen{terminKopf ? ` — ${terminKopf.betreff}` : ""}
                    </h1>
                    {terminKopf && <div className="cal-notiz-zeit">{terminKopf.zeitraum}</div>}
                </div>
                <div className="cal-notiz-kopf-rechts">
                    <span
                        className={`cal-notiz-status${status === "fehler" ? " ist-fehler" : ""}`}
                        role="status"
                        title={statusTitel[status] || undefined}
                    >
                        {statusLabel[status]}
                    </span>
                    {seite && termin && (
                        <button type="button" className="cal-notiz-btn" onClick={zeigeTermin}>
                            <CalendarLtr20Regular aria-hidden /> Termin anzeigen
                        </button>
                    )}
                    {canWrite && (
                        <button
                            type="button"
                            className="cal-notiz-btn cal-notiz-btn-primary"
                            disabled={status === "speichert"}
                            onClick={() => void speichern()}
                        >
                            Speichern
                        </button>
                    )}
                </div>
            </div>

            {!canWrite && seite && (
                <div className="cal-notiz-readonly">
                    Nur Lesezugriff — Inhalte sind sichtbar, Ändern ist gesperrt.
                </div>
            )}

            {/* Werkzeugleiste: Text-Formatierung + Stift/Radierer (nur WRITE). */}
            {canWrite && (
                <div className="cal-notiz-toolbar">
                    <div className="cal-notiz-toolgruppe" aria-label="Text">
                        <button
                            type="button"
                            className={`cal-notiz-tool${werkzeug === "text" ? " ist-aktiv" : ""}`}
                            title="Text schreiben"
                            onClick={() => setWerkzeug("text")}
                        >
                            Text
                        </button>
                        <button
                            type="button"
                            className="cal-notiz-tool"
                            title="Fett"
                            disabled={werkzeug !== "text"}
                            onMouseDown={(e) => e.preventDefault() /* Selektion behalten */}
                            onClick={() => {
                                if (editorRef.current) {
                                    toggleInlineFormat(editorRef.current, "strong");
                                    markDirty();
                                }
                            }}
                        >
                            <TextBold20Regular aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="cal-notiz-tool"
                            title="Kursiv"
                            disabled={werkzeug !== "text"}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                if (editorRef.current) {
                                    toggleInlineFormat(editorRef.current, "em");
                                    markDirty();
                                }
                            }}
                        >
                            <TextItalic20Regular aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="cal-notiz-tool"
                            title="Aufzählung"
                            disabled={werkzeug !== "text"}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                if (editorRef.current) {
                                    fuegeListeEin(editorRef.current);
                                    markDirty();
                                }
                            }}
                        >
                            <TextBulletListLtr20Regular aria-hidden />
                        </button>
                    </div>
                    <div className="cal-notiz-toolgruppe" aria-label="Zeichnen">
                        {NOTIZ_STIFT_FARBEN.map((f) => (
                            <button
                                key={f.hex}
                                type="button"
                                className={`cal-notiz-stift${
                                    werkzeug === "stift" && stiftFarbe === f.hex ? " ist-aktiv" : ""
                                }`}
                                title={`Stift ${f.label}`}
                                onClick={() => {
                                    setWerkzeug("stift");
                                    setStiftFarbe(f.hex);
                                }}
                            >
                                <span
                                    aria-hidden
                                    className="cal-notiz-stift-punkt"
                                    style={{ background: f.hex }}
                                />
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`cal-notiz-tool${werkzeug === "radierer" ? " ist-aktiv" : ""}`}
                            title="Radierer (ganze Striche löschen)"
                            onClick={() => setWerkzeug("radierer")}
                        >
                            <Eraser20Regular aria-hidden />
                        </button>
                    </div>
                </div>
            )}

            {/* Freie Seite: contentEditable-Text + Zeichen-Canvas als Layer darüber. */}
            <div
                ref={flaecheRef}
                className="cal-notiz-flaeche"
                style={{ minHeight: flaecheMinHoehe }}
            >
                <div
                    ref={editorRef}
                    className="cal-notiz-editor"
                    contentEditable={canWrite && werkzeug === "text"}
                    suppressContentEditableWarning
                    data-placeholder="Notizen zum Termin…"
                    onInput={markDirty}
                    // Aufzählungen beim Tippen (PO 14.09.2026): „- " oder „1. "
                    // + Leertaste beginnt eine Liste, Tab/Shift+Tab rückt ein
                    // und aus, Enter auf leerem Punkt verlässt sie. Dieselbe
                    // Funktion wie im Mail-Editor und in den V4-Notizblöcken.
                    onKeyDown={(e) => {
                        if (!canWrite) return;
                        behandleListenTaste(editorRef.current, e, markDirty);
                    }}
                    onBlur={() => {
                        // Fokus-Verlust = natürlicher Speicherpunkt.
                        if (status === "dirty") void speichern();
                    }}
                />
                <canvas
                    ref={canvasRef}
                    className={`cal-notiz-canvas${
                        canWrite && werkzeug !== "text" ? " ist-zeichnen" : ""
                    }`}
                    style={{ width: canvasGroesse.w, height: canvasGroesse.h }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                />
            </div>

            {/* Vorgang: PO 14.09.2026 — „unten ein Button unter der Notiz, zu einem
                Vorgang zuweisen". Gebunden wird der TERMIN (Sidecar-Slot) — an den
                Vorgang UND, sobald der Vorgang To-dos hat, an EINES davon (oder
                ein neues). Damit steht diese Notiz als Karte im Notizstrom des
                Vorgangs (V4), im Strang des gewählten To-dos. */}
            {seite && (
                <div className="cal-notiz-doks">
                    <div className="cal-notiz-doks-titel">Vorgang</div>
                    {meta?.vorgangId != null ? (
                        <div className="cal-notiz-doks-liste">
                            <div className="cal-notiz-dok">
                                <button
                                    type="button"
                                    className="cal-notiz-dok-name"
                                    onClick={() => navigate(`/vorgaenge/${meta.vorgangId}`)}
                                >
                                    {meta.vorgangTitel?.trim() || `Vorgang #${meta.vorgangId}`}
                                </button>
                                {/* Das To-do steht hinter dem Vorgang, weil es
                                    zu ihm gehört — und weil der Sprung derselbe
                                    ist: die Vorgangsseite zeigt die Karte im
                                    Strang dieses To-dos. */}
                                {meta.todoId != null && (
                                    <span className="cal-notiz-todo">
                                        To-do: {todoTitel || `#${meta.todoId}`}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="cal-notiz-doks-leer">Noch keinem Vorgang zugewiesen.</div>
                    )}
                    {canLink && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="cal-notiz-btn"
                                onClick={() => setZuweisenOffen(true)}
                            >
                                {meta?.vorgangId != null
                                    ? "Anderem Vorgang zuweisen"
                                    : "Zu einem Vorgang zuweisen"}
                            </button>
                            <button
                                type="button"
                                className="cal-notiz-btn"
                                onClick={() => setNeuerVorgangOffen(true)}
                            >
                                Neuer Vorgang aus Termin
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Dokumente (Reuse documentService; erscheinen auch im Termin-Sidecar). */}
            <div className="cal-notiz-doks">
                <div className="cal-notiz-doks-titel">Dokumente</div>
                {docs.length === 0 ? (
                    <div className="cal-notiz-doks-leer">Keine Dokumente.</div>
                ) : (
                    <div className="cal-notiz-doks-liste">
                        {docs.map((d) => (
                            <div key={d.id} className="cal-notiz-dok">
                                <button
                                    type="button"
                                    className="cal-notiz-dok-name"
                                    onClick={() => void downloadDoc(d.id)}
                                >
                                    {d.fileName}
                                </button>
                                {canWriteKalender && (
                                    <button
                                        type="button"
                                        className="cal-notiz-dok-loeschen"
                                        onClick={() => void removeDoc(d.id)}
                                    >
                                        Löschen
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {canWriteKalender && (
                    <>
                        <button
                            type="button"
                            className="cal-notiz-btn"
                            disabled={uploading}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <DocumentAdd20Regular aria-hidden />
                            {uploading ? "Lade hoch…" : "Datei hochladen"}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: "none" }}
                            onChange={(e) => void handleFile(e)}
                        />
                    </>
                )}
            </div>

            <TerminPeekPopover
                peek={peek}
                onClose={() => setPeek(null)}
                // Loeschen nur mit Schreibrecht auf dem Postfach — dieselbe
                // Auskunft, die schon die Notiz-Werkzeuge steuert.
                canEditEvent={() => canWriteKalender}
                onOpenEdit={oeffneTerminFenster}
            />
            <VorgangZuweisenDialog
                kontext={zuweisenOffen ? vorgangKontext : null}
                onClose={() => setZuweisenOffen(false)}
                onGebunden={() => {
                    setZuweisenOffen(false);
                    metaNeuLesen();
                }}
            />
            <NeuerVorgangAusTerminDialog
                kontext={neuerVorgangOffen ? vorgangKontext : null}
                onClose={() => setNeuerVorgangOffen(false)}
                onGebunden={() => {
                    setNeuerVorgangOffen(false);
                    metaNeuLesen();
                }}
            />
        </div>
    );
};

export default TerminNotizenPage;
